import React, { useState, useEffect } from "react";
import { Button, Link } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import AppTable from "../../common/app-table";
import { formatWithTzLabel } from "../../utils/dateUtils";

const CustomersTable = ({ searchInput, filters = {}, handleEditClick, onViewClient }) => {
  const [rowCount, setRowCount] = useState(0);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });

  const permissions = localStorage.getItem("permissions") || [];
  const hasEditPermission = permissions.includes("client_master.change");

  const URL =
    process.env.NODE_ENV === "production" ? "api" : "http://localhost:8000/api";

  // Reset to page 0 whenever search changes
  useEffect(() => {
    setController({ page: 0, rowsPerPage: 20 });
  }, [searchInput, filters]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        const user = JSON.parse(localStorage.getItem("user"));
        const tenant = localStorage.getItem("tenant");

        params.append("user_id", user.id);
        params.append("limit", controller.rowsPerPage);
        params.append("offset", controller.page * controller.rowsPerPage);
        if (searchInput) params.append("search_value", searchInput);
        if (filters.updatedDate) params.append("updated_date", filters.updatedDate);

        const response = await fetch(`${URL}/clients/clients/?${params.toString()}`, {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch data");
        const jsonData = await response.json();
        setHoldings(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [controller, searchInput, filters]);

  const handlePageChange = (event, newPage) =>
    setController((prev) => ({ ...prev, page: newPage }));
  const handleChangeRowsPerPage = (event) =>
    setController({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

  // ── Column definitions ──────────────────────────────────────────────────────

  const tableColumns = [
    {
      id: "phone_no",
      label: "Client Phone No",
      render: (client) => client.phone_no,
    },
    {
      id: "client_code",
      label: "Client Code",
      render: (client) => (
        <Link
          href="#"
          onClick={() => onViewClient(client.client_code)}
          underline="none"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
        >
          {client.client_code || "N/A"}
        </Link>
      ),
    },
    { id: "client_name", label: "Client Name" },
    {
      id: "date_of_birth",
      label: "Date of Birth",
      render: (client) =>
        client.date_of_birth ? dayjs(client.date_of_birth).format("DD/MM/YYYY") : "-",
    },
    { id: "reporting_manager_name", label: "RM Name" },
    { id: "reporting_manager", label: "RM Phone No" },
    {
      id: "updated_at",
      label: "Updated On",
      render: (client) => (
        <span
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          {formatWithTzLabel(client.updated_at_iso)}
          {hasEditPermission && (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(e, client);
              }}
            >
              <EditIcon fontSize="small" />
            </Button>
          )}
        </span>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <AppTable
      columns={tableColumns}
      rows={holdings}
      rowKey={(client, index) => client.id ?? index}
      loading={loading}
      emptyMessage="No match found"
      maxHeight={490}
      hideMainScroll={false}
      paperSx={{ width: "auto !important" }}
      count={rowCount}
      page={controller.page}
      rowsPerPage={controller.rowsPerPage}
      rowsPerPageOptions={[20]}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
  );
};

export default CustomersTable;